import { useMemo } from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Chip,
  IconButton,
  Link,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid';

import { StatusChip } from '@/components/common/StatusChip';
import type { DateRangePreset, ScheduleSummary, TriggerDetail } from '@/types/schedule';

import {
  formatDateForDisplay,
  getDateRangeFromPreset,
  getExecutionHistoryDateRangePresets,
} from '../utils/dateRanges';
import { formatExecutionHistoryDateTime } from '../utils/scheduleDateTime';

const getExecutionChipColor = (status?: string): 'success' | 'error' | 'info' | 'default' => {
  if (status === 'Succeeded') return 'success';
  if (status === 'Failed') return 'error';
  if (status === 'Running') return 'info';
  return 'default';
};

interface ScheduleDetailsGridProps {
  details: TriggerDetail[];
  totalCount: number;
  isLoading?: boolean;
  paginationModel: GridPaginationModel;
  onPaginationChange: (model: GridPaginationModel) => void;
  dateRangePreset: DateRangePreset;
  onDateRangeChange: (preset: DateRangePreset) => void;
  onRefresh?: () => void;
  appId?: string;
  schedules?: ScheduleSummary[];
}

// Status cell renderer
const StatusCell = ({
  status,
  externalExecutionId,
  environmentId,
  appId,
}: {
  status?: string;
  externalExecutionId?: string;
  environmentId?: string;
  appId?: string;
}) => {
  if (!status) {
    return <Typography variant="body2">-</Typography>;
  }

  const chip = <StatusChip status={status} />;

  if (externalExecutionId && appId) {
    const cleanId = externalExecutionId.replace(/[\\/]+$/, '');
    return (
      <Link
        href={`/a/${appId}/health?executionGuid=${cleanId}&env=${environmentId}`}
        underline="hover"
      >
        {chip}
      </Link>
    );
  }

  return chip;
};

export const ScheduleDetailsGrid = ({
  details,
  totalCount,
  isLoading = false,
  paginationModel,
  onPaginationChange,
  dateRangePreset,
  onDateRangeChange,
  onRefresh,
  appId,
  schedules = [],
}: ScheduleDetailsGridProps) => {
  const dateRangePresets = getExecutionHistoryDateRangePresets();

  // Add ID to rows (server already filters by environment via envId query param)
  const rowsWithId = useMemo(() => {
    return details.map((detail, index) => ({
      ...detail,
      id: detail.externalExecutionId || `${detail.baseId}-${detail.executionTime}-${index}`,
    }));
  }, [details]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'executionTime',
        headerName: 'Execution Date/Time (UTC)',
        width: 220,
        display: 'flex',
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as TriggerDetail;
          return (
            <Typography variant="body2">
              {params.value ? formatExecutionHistoryDateTime(row, schedules) : '-'}
            </Typography>
          );
        },
      },
      {
        field: 'transformerName',
        headerName: 'Recipe',
        flex: 1,
        minWidth: 180,
        display: 'flex',
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" noWrap>
            {params.value || '-'}
          </Typography>
        ),
      },
      {
        field: 'environmentName',
        headerName: 'Environment',
        width: 140,
        display: 'flex',
        renderCell: (params: GridRenderCellParams) => (
          <Chip label={params.value || '-'} size="small" color="secondary" variant="outlined" />
        ),
      },
      {
        field: 'executionStatus',
        headerName: 'Status',
        width: 120,
        display: 'flex',
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as TriggerDetail;
          return (
            <StatusCell
              status={params.value}
              externalExecutionId={row.externalExecutionId}
              environmentId={row.environmentId}
              appId={appId}
            />
          );
        },
      },
    ],
    [appId, schedules]
  );

  const handleDateRangeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPreset: DateRangePreset | null
  ) => {
    if (newPreset !== null) {
      onDateRangeChange(newPreset);
    }
  };

  // Get current date range info
  const currentRange = getDateRangeFromPreset(dateRangePreset);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Date Range Filter */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" color="text.secondary">
            Execution History
          </Typography>
          {onRefresh && (
            <Tooltip title="Refresh">
              <Box component="span" sx={{ display: 'inline-block' }}>
                <IconButton size="small" onClick={onRefresh} disabled={isLoading}>
                  <RefreshIcon
                    fontSize="small"
                    sx={{
                      animation: isLoading ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </IconButton>
              </Box>
            </Tooltip>
          )}
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <ToggleButtonGroup
            value={dateRangePreset}
            exclusive
            onChange={handleDateRangeChange}
            size="small"
          >
            {dateRangePresets.map((preset) => (
              <ToggleButton key={preset.value} value={preset.value}>
                {preset.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Stack>

      {/* Date Range Info */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Showing executions from {formatDateForDisplay(currentRange.startDate)} to{' '}
        {formatDateForDisplay(currentRange.endDate)}
      </Typography>

      {/* Data Grid */}
      <DataGrid
        rows={rowsWithId}
        columns={columns}
        loading={isLoading}
        paginationMode="server"
        rowCount={totalCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationChange}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
        }}
      />
    </Box>
  );
};

// Compact version for embedding in schedule summary view
interface ScheduleDetailsCompactProps {
  details: TriggerDetail[];
  totalCount: number;
  isLoading?: boolean;
  onViewMore?: () => void;
  schedules?: ScheduleSummary[];
}

export const ScheduleDetailsCompact = ({
  details,
  totalCount,
  isLoading = false,
  onViewMore,
  schedules = [],
}: ScheduleDetailsCompactProps) => {
  const displayedDetails = details.slice(0, 5);

  const renderDetailsContent = () => {
    if (isLoading) {
      return (
        <Stack spacing={1}>
          {[1, 2, 3].map((i) => (
            <Stack
              key={i}
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}
            >
              <Skeleton variant="text" width={140} />
              <Skeleton variant="text" sx={{ flex: 1 }} />
              <Skeleton variant="rounded" width={72} height={24} />
            </Stack>
          ))}
        </Stack>
      );
    }

    if (displayedDetails.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No recent executions
        </Typography>
      );
    }

    return (
      <Stack spacing={1}>
        {displayedDetails.map((detail, index) => (
          <Stack
            key={detail.externalExecutionId || index}
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="body2" sx={{ minWidth: 180 }}>
              {formatExecutionHistoryDateTime(detail, schedules)}
            </Typography>
            <Typography variant="body2" sx={{ flex: 1 }} noWrap>
              {detail.transformerName}
            </Typography>
            <Chip
              label={detail.executionStatus || 'Unknown'}
              size="small"
              color={getExecutionChipColor(detail.executionStatus)}
            />
          </Stack>
        ))}
      </Stack>
    );
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Recent Executions</Typography>
        {totalCount > 5 && onViewMore && (
          <Link component="button" variant="body2" onClick={onViewMore} sx={{ cursor: 'pointer' }}>
            View all ({totalCount})
          </Link>
        )}
      </Stack>

      {renderDetailsContent()}
    </Box>
  );
};
